import { useEffect } from 'react';
import { useChat } from '../hooks/useChat';
import type { PublicStoreConfig, ProductCard as ProductCardData } from '../types';
import StoreHeader from './StoreHeader';
import MessageList from './MessageList';
import InputBar from './InputBar';
import CartSummary from './CartSummary';

interface Props {
  slug: string;
  config: PublicStoreConfig | null;
  onClose?: () => void;
  /** Narrow widget mode; standalone (false) centers content at a desktop width. */
  embedded?: boolean;
}

export function ChatWindow({ slug, config, onClose, embedded }: Props) {
  const storeName = config?.name ?? 'Store';
  const storeInitial = storeName.trim().charAt(0).toUpperCase() || 'S';
  const personaAvatar = config?.aiPersonaEmoji || storeInitial;

  const contentClass = embedded ? 'w-full' : 'mx-auto w-full max-w-3xl';

  const {
    turns,
    cart,
    awaitingReply,
    connectionState,
    sendUserMessage,
    addToCart,
    updateCartItem,
    removeFromCart,
    checkout,
  } = useChat(slug, true);

  const offline = connectionState === 'reconnecting' || connectionState === 'closed';

  const handleViewMore = (product: ProductCardData) => {
    sendUserMessage(`Tell me more about ${product.name}`);
  };

  // Emit cart changes so the ChatFab badge stays in sync.
  useEffect(() => {
    const count = cart.reduce((n, i) => n + i.quantity, 0);
    window.dispatchEvent(
      new CustomEvent('cartiq:cart-update', { detail: { cartCount: count } }),
    );
  }, [cart]);

  // Listen for "ask about" messages dispatched from the store page.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.message) {
        sendUserMessage(detail.message);
      }
    };
    window.addEventListener('cartiq:send-message', handler);
    return () => window.removeEventListener('cartiq:send-message', handler);
  }, [sendUserMessage]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-surface">
      <StoreHeader config={config} storeName={storeName} onClose={onClose} contentClass={contentClass} />

      {offline && (
        <div className="bg-warning/10 px-4 py-1.5 text-center text-[11px] font-medium text-warning">
          {connectionState === 'reconnecting' ? 'Reconnecting…' : 'Connection lost'}
        </div>
      )}

      <MessageList
        turns={turns}
        awaitingReply={awaitingReply}
        slug={slug}
        storeInitial={storeInitial}
        personaAvatar={personaAvatar}
        storeName={storeName}
        config={config}
        onAddToCart={addToCart}
        onViewMore={handleViewMore}
        onPick={sendUserMessage}
        contentClass={contentClass}
      />

      <CartSummary
        cart={cart}
        onCheckout={checkout}
        onUpdateQuantity={updateCartItem}
        onRemove={removeFromCart}
        billing={config?.billingConfig}
        contentClass={contentClass}
      />

      <InputBar
        onSend={sendUserMessage}
        disabled={awaitingReply}
        placeholder={awaitingReply ? `${storeName} is typing…` : `Message ${storeName}…`}
        contentClass={contentClass}
      />
    </div>
  );
}

export default ChatWindow;